import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Clock, Star, GripVertical, User, DollarSign, MapPin, RotateCcw } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/hooks/use-workspace";
import type { Talent, Region, EventOfferingMarketPricing, ClientTypeRecord } from "@shared/schema";

interface EventOffering {
  id: string;
  service: string;
  name: string;
  rate: string | null;
  minutesBefore: number;
  minutesAfter: number;
  isDefault: boolean;
  hasDynamicPricing: boolean;
  noSetRate: boolean;
  sortOrder: number;
  talentId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EventOfferingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  talentId?: string;
  talentName?: string;
}

export function EventOfferingsDialog({ open, onOpenChange, talentId: propTalentId, talentName }: EventOfferingsDialogProps) {
  const { toast } = useToast();
  // Task #107 — workspace-scoped writes must carry the /w/:slug prefix.
  const { buildPath } = useWorkspace();
  const [editingOffering, setEditingOffering] = useState<EventOffering | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTalentId, setSelectedTalentId] = useState<string>(propTalentId || "all");
  const [formData, setFormData] = useState({
    service: "Entertainment Performance",
    name: "",
    rate: "0",
    minutesBefore: 30,
    minutesAfter: 60,
    isDefault: false,
    hasDynamicPricing: false,
    noSetRate: false,
    sortOrder: 0,
    talentId: propTalentId || "",
  });
  const [dynamicPricingDialogOpen, setDynamicPricingDialogOpen] = useState(false);
  const [dynamicPricingOfferingId, setDynamicPricingOfferingId] = useState<string | null>(null);

  // When propTalentId changes, update the filter and form default
  useEffect(() => {
    if (propTalentId) {
      setSelectedTalentId(propTalentId);
      setFormData(prev => ({ ...prev, talentId: propTalentId }));
    } else {
      setSelectedTalentId("all");
    }
  }, [propTalentId]);

  const { data: offerings = [], isLoading } = useQuery<EventOffering[]>({
    queryKey: ["/api/event-offerings"],
    enabled: open,
  });

  const { data: talent = [] } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
    enabled: open,
  });

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    enabled: open,
  });

  const hasMultipleTalent = talent.length > 1;
  const defaultTalent = talent.find(e => e.isDefault);

  const filteredOfferings = selectedTalentId === "all" 
    ? offerings 
    : offerings.filter(o => o.talentId === selectedTalentId || (!o.talentId && selectedTalentId === (defaultTalent?.id || "")));

  const createMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const response = await apiRequest("POST", buildPath("/api/event-offerings"), data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      toast({ title: "Event Offering Created", description: "The new show offering has been created." });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create show offering.", variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof formData }) => {
      const response = await apiRequest("PATCH", buildPath(`/api/event-offerings/${id}`), data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      toast({ title: "Event Offering Updated", description: "The event offering has been updated." });
      resetForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update show offering.", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", buildPath(`/api/event-offerings/${id}`));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings/deleted"] });
      toast({ title: "Event Offering Deleted", description: "Moved to Recently Deleted — restore within 90 days." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete show offering.", variant: "destructive" });
    },
  });

  // Task #111 — Recently Deleted list + restore.
  const { data: deletedOfferings = [] } = useQuery<Array<EventOffering & { deletedAt: string | null; deletedBy: string | null; deletedByEmail: string | null }>>({
    queryKey: ["/api/event-offerings/deleted"],
    enabled: open,
  });

  const filteredDeletedOfferings = selectedTalentId === "all"
    ? deletedOfferings
    : deletedOfferings.filter(o => o.talentId === selectedTalentId || (!o.talentId && selectedTalentId === (defaultTalent?.id || "")));

  const restoreMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest("POST", buildPath(`/api/event-offerings/${id}/restore`));
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings"] });
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings/deleted"] });
      toast({ title: "Event Offering Restored", description: "The event offering and its pricing rules are back." });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to restore event offering.", variant: "destructive" });
    },
  });

  const resetForm = () => {
    setFormData({ 
      service: "Entertainment Performance", 
      name: "", 
      rate: "0", 
      minutesBefore: 30, 
      minutesAfter: 60, 
      isDefault: false, 
      hasDynamicPricing: false,
      noSetRate: false,
      sortOrder: 0,
      talentId: propTalentId || defaultTalent?.id || "",
    });
    setEditingOffering(null);
    setIsCreating(false);
  };

  const startEdit = (offering: EventOffering) => {
    setEditingOffering(offering);
    setFormData({
      service: offering.service || "Entertainment Performance",
      name: offering.name,
      rate: offering.rate || "0",
      minutesBefore: offering.minutesBefore,
      minutesAfter: offering.minutesAfter,
      isDefault: offering.isDefault,
      hasDynamicPricing: offering.hasDynamicPricing || false,
      noSetRate: (offering as any).noSetRate || false,
      sortOrder: offering.sortOrder,
      talentId: offering.talentId || defaultTalent?.id || "",
    });
    setIsCreating(false);
  };

  const openDynamicPricingDialog = (offeringId: string) => {
    setDynamicPricingOfferingId(offeringId);
    setDynamicPricingDialogOpen(true);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  const handleSave = () => {
    if (!formData.name.trim()) {
      toast({ title: "Error", description: "Name is required.", variant: "destructive" });
      return;
    }
    
    if (editingOffering) {
      updateMutation.mutate({ id: editingOffering.id, data: formData });
    } else {
      createMutation.mutate({ ...formData, sortOrder: offerings.length });
    }
  };

  const getTotalDuration = (before: number, after: number) => {
    return before + after;
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {talentName ? `Event Offerings for ${talentName}` : "Manage Event Offerings"}
          </DialogTitle>
          <DialogDescription>
            Configure event types and their calendar booking durations. These settings control how long calendar events are blocked for each event type.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {hasMultipleTalent && !propTalentId && (
            <div className="flex items-center gap-2">
              <Label className="shrink-0">Filter by Talent:</Label>
              <Select value={selectedTalentId} onValueChange={setSelectedTalentId}>
                <SelectTrigger className="w-[200px]" data-testid="select-filter-talent">
                  <SelectValue placeholder="All Talent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Talent</SelectItem>
                  {talent.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name} {e.isDefault && "(Default)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-4 text-muted-foreground">Loading...</div>
          ) : (
            <>
              {filteredOfferings.length === 0 && !isCreating && (
                <div className="text-center py-8 text-muted-foreground">
                  No show offerings configured. Add your first one below.
                </div>
              )}

              {filteredOfferings.map((offering) => (
                <Card key={offering.id} className={editingOffering?.id === offering.id ? "ring-2 ring-primary" : ""}>
                  <CardContent className="p-4">
                    {editingOffering?.id === offering.id ? (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="col-span-2">
                            <Label htmlFor="edit-service">Service</Label>
                            <Input
                              id="edit-service"
                              value={formData.service}
                              onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                              placeholder="e.g., Entertainment Performance"
                              data-testid="input-offering-service"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Displayed on invoices</p>
                          </div>
                          <div>
                            <Label htmlFor="edit-name">Description</Label>
                            <Input
                              id="edit-name"
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder="e.g., 50 Minute Show"
                              data-testid="input-offering-name"
                            />
                          </div>
                          <div>
                            <Label htmlFor="edit-rate">Rate ($)</Label>
                            {formData.noSetRate ? (
                              <div className="h-9 px-3 flex items-center text-sm text-muted-foreground bg-muted/50 rounded-md border" data-testid="text-offering-rate-disabled">
                                — (entered per booking)
                              </div>
                            ) : (
                              <Input
                                id="edit-rate"
                                type="number"
                                min={0}
                                step="0.01"
                                value={formData.rate}
                                onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                                placeholder="0.00"
                                data-testid="input-offering-rate"
                              />
                            )}
                          </div>
                          <div>
                            <Label htmlFor="edit-before">Time Before Show (min)</Label>
                            <Input
                              id="edit-before"
                              type="number"
                              min={0}
                              value={formData.minutesBefore}
                              onChange={(e) => setFormData({ ...formData, minutesBefore: parseInt(e.target.value) || 0 })}
                              data-testid="input-minutes-before"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Setup/arrival time</p>
                          </div>
                          <div>
                            <Label htmlFor="edit-after">Time After Show (min)</Label>
                            <Input
                              id="edit-after"
                              type="number"
                              min={0}
                              value={formData.minutesAfter}
                              onChange={(e) => setFormData({ ...formData, minutesAfter: parseInt(e.target.value) || 0 })}
                              data-testid="input-minutes-after"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Event Length + Teardown</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="edit-default"
                            checked={formData.isDefault}
                            onCheckedChange={(checked) => setFormData({ ...formData, isDefault: !!checked })}
                          />
                          <Label htmlFor="edit-default" className="text-sm">Set as default offering</Label>
                        </div>
                        <div className="flex items-center gap-2 p-3 border rounded-md">
                          <Checkbox
                            id="edit-no-set-rate"
                            checked={formData.noSetRate}
                            onCheckedChange={(checked) => {
                              const next = !!checked;
                              setFormData({
                                ...formData,
                                noSetRate: next,
                                // Turning this on disables dynamic pricing (no base rate to scale from)
                                hasDynamicPricing: next ? false : formData.hasDynamicPricing,
                                rate: next ? "0" : formData.rate,
                              });
                            }}
                            data-testid="checkbox-no-set-rate"
                          />
                          <div>
                            <Label htmlFor="edit-no-set-rate" className="text-sm font-medium">No Set Rate (Custom Show)</Label>
                            <p className="text-xs text-muted-foreground">Rate is entered manually for each booking — useful for custom corporate shows or ticketed events</p>
                          </div>
                        </div>
                        {!formData.noSetRate && (
                        <div className="flex items-center justify-between gap-2 p-3 border rounded-md">
                          <div className="flex items-center gap-2">
                            <Checkbox
                              id="edit-dynamic-pricing"
                              checked={formData.hasDynamicPricing}
                              onCheckedChange={(checked) => {
                                setFormData({ ...formData, hasDynamicPricing: !!checked });
                                if (checked && editingOffering) {
                                  openDynamicPricingDialog(editingOffering.id);
                                }
                              }}
                              data-testid="checkbox-dynamic-pricing"
                            />
                            <div>
                              <Label htmlFor="edit-dynamic-pricing" className="text-sm font-medium">Dynamic Pricing</Label>
                              <p className="text-xs text-muted-foreground">Set different prices or availability by market</p>
                            </div>
                          </div>
                          {formData.hasDynamicPricing && editingOffering && (
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => openDynamicPricingDialog(editingOffering.id)}
                              data-testid="button-configure-pricing"
                            >
                              <MapPin className="w-3 h-3 mr-1" />
                              Configure
                            </Button>
                          )}
                        </div>
                        )}
                        {hasMultipleTalent && !propTalentId && (
                          <div>
                            <Label htmlFor="edit-talent">Talent</Label>
                            <Select
                              value={formData.talentId || defaultTalent?.id || ""}
                              onValueChange={(value) => setFormData({ ...formData, talentId: value })}
                            >
                              <SelectTrigger data-testid="select-offering-talent">
                                <SelectValue placeholder="Select talent" />
                              </SelectTrigger>
                              <SelectContent>
                                {talent.map((e) => (
                                  <SelectItem key={e.id} value={e.id}>
                                    {e.name} {e.isDefault && "(Default)"}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div className="p-3 bg-muted rounded-md">
                          <div className="flex items-center gap-2 text-sm">
                            <Clock className="w-4 h-4" />
                            <span>Total calendar block: <strong>{formatDuration(getTotalDuration(formData.minutesBefore, formData.minutesAfter))}</strong></span>
                          </div>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" size="sm" onClick={resetForm} data-testid="button-cancel-edit">
                            Cancel
                          </Button>
                          <Button 
                            size="sm" 
                            onClick={handleSave} 
                            disabled={updateMutation.isPending}
                            data-testid="button-save-offering"
                          >
                            {updateMutation.isPending ? "Saving..." : "Save Changes"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium">{offering.name}</span>
                              {(offering as any).noSetRate ? (
                                <Badge variant="outline" className="text-xs font-semibold">
                                  Custom Rate
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-xs font-semibold">
                                  ${parseFloat(offering.rate || "0").toFixed(2)}
                                </Badge>
                              )}
                              {offering.isDefault && (
                                <Badge variant="secondary" className="text-xs">
                                  <Star className="w-3 h-3 mr-1" />
                                  Default
                                </Badge>
                              )}
                              {hasMultipleTalent && offering.talentId && (
                                <Badge variant="outline" className="text-xs">
                                  <User className="w-3 h-3 mr-1" />
                                  {talent.find(e => e.id === offering.talentId)?.name || "Unknown"}
                                </Badge>
                              )}
                              {offering.hasDynamicPricing && (
                                <Badge variant="secondary" className="text-xs">
                                  <DollarSign className="w-3 h-3 mr-1" />
                                  Dynamic Pricing
                                </Badge>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground mb-1">
                              Invoice Service: {offering.service || "Entertainment Performance"}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Clock className="w-3 h-3" />
                              {formatDuration(offering.minutesBefore)} before + {formatDuration(offering.minutesAfter)} after
                              <span className="text-muted-foreground/60">
                                ({formatDuration(getTotalDuration(offering.minutesBefore, offering.minutesAfter))} total)
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => startEdit(offering)}
                            data-testid={`button-edit-offering-${offering.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => deleteMutation.mutate(offering.id)}
                            disabled={deleteMutation.isPending}
                            data-testid={`button-delete-offering-${offering.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {isCreating && (
                <Card className="ring-2 ring-primary">
                  <CardContent className="p-4">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="col-span-2">
                          <Label htmlFor="new-service">Service</Label>
                          <Input
                            id="new-service"
                            value={formData.service}
                            onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                            placeholder="e.g., Entertainment Performance"
                            data-testid="input-new-offering-service"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Displayed on invoices</p>
                        </div>
                        <div>
                          <Label htmlFor="new-name">Description</Label>
                          <Input
                            id="new-name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., 50 Minute Show"
                            data-testid="input-new-offering-name"
                          />
                        </div>
                        <div>
                          <Label htmlFor="new-rate">Rate ($)</Label>
                          {formData.noSetRate ? (
                            <div className="h-9 px-3 flex items-center text-sm text-muted-foreground bg-muted/50 rounded-md border" data-testid="text-new-offering-rate-disabled">
                              — (entered per booking)
                            </div>
                          ) : (
                            <Input
                              id="new-rate"
                              type="number"
                              min={0}
                              step="0.01"
                              value={formData.rate}
                              onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                              placeholder="0.00"
                              data-testid="input-new-offering-rate"
                            />
                          )}
                        </div>
                        <div>
                          <Label htmlFor="new-before">Time Before Show (min)</Label>
                          <Input
                            id="new-before"
                            type="number"
                            min={0}
                            value={formData.minutesBefore}
                            onChange={(e) => setFormData({ ...formData, minutesBefore: parseInt(e.target.value) || 0 })}
                            data-testid="input-new-minutes-before"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Setup/arrival time</p>
                        </div>
                        <div>
                          <Label htmlFor="new-after">Time After Show (min)</Label>
                          <Input
                            id="new-after"
                            type="number"
                            min={0}
                            value={formData.minutesAfter}
                            onChange={(e) => setFormData({ ...formData, minutesAfter: parseInt(e.target.value) || 0 })}
                            data-testid="input-new-minutes-after"
                          />
                          <p className="text-xs text-muted-foreground mt-1">Event Length + Teardown</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="new-default"
                          checked={formData.isDefault}
                          onCheckedChange={(checked) => setFormData({ ...formData, isDefault: !!checked })}
                        />
                        <Label htmlFor="new-default" className="text-sm">Set as default offering</Label>
                      </div>
                      <div className="flex items-center gap-2 p-3 border rounded-md">
                        <Checkbox
                          id="new-no-set-rate"
                          checked={formData.noSetRate}
                          onCheckedChange={(checked) => {
                            const next = !!checked;
                            setFormData({
                              ...formData,
                              noSetRate: next,
                              rate: next ? "0" : formData.rate,
                            });
                          }}
                          data-testid="checkbox-new-no-set-rate"
                        />
                        <div>
                          <Label htmlFor="new-no-set-rate" className="text-sm font-medium">No Set Rate (Custom Show)</Label>
                          <p className="text-xs text-muted-foreground">Rate is entered manually for each booking — useful for custom corporate shows or ticketed events</p>
                        </div>
                      </div>
                      {!formData.noSetRate && (
                      <div className="flex items-center gap-2 p-3 border rounded-md opacity-60">
                        <Checkbox
                          id="new-dynamic-pricing"
                          checked={false}
                          disabled
                        />
                        <div>
                          <Label htmlFor="new-dynamic-pricing" className="text-sm font-medium">Dynamic Pricing</Label>
                          <p className="text-xs text-muted-foreground">Save first, then configure market-specific pricing</p>
                        </div>
                      </div>
                      )}
                      {hasMultipleTalent && !propTalentId && (
                        <div>
                          <Label htmlFor="new-talent">Talent</Label>
                          <Select
                            value={formData.talentId || defaultTalent?.id || ""}
                            onValueChange={(value) => setFormData({ ...formData, talentId: value })}
                          >
                            <SelectTrigger data-testid="select-new-offering-talent">
                              <SelectValue placeholder="Select talent" />
                            </SelectTrigger>
                            <SelectContent>
                              {talent.map((e) => (
                                <SelectItem key={e.id} value={e.id}>
                                  {e.name} {e.isDefault && "(Default)"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      <div className="p-3 bg-muted rounded-md">
                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4" />
                          <span>Total calendar block: <strong>{formatDuration(getTotalDuration(formData.minutesBefore, formData.minutesAfter))}</strong></span>
                        </div>
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button variant="outline" size="sm" onClick={resetForm} data-testid="button-cancel-create">
                          Cancel
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={handleSave} 
                          disabled={createMutation.isPending}
                          data-testid="button-create-offering"
                        >
                          {createMutation.isPending ? "Creating..." : "Create Offering"}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!isCreating && !editingOffering && (
                <Button 
                  variant="outline" 
                  className="w-full" 
                  onClick={startCreate}
                  data-testid="button-add-offering"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Event Offering
                </Button>
              )}

              {/* Task #111 — Recently Deleted (90-day window). Restoring also
                  brings back the offering's dynamic pricing rows. */}
              {filteredDeletedOfferings.length > 0 && (
                <div className="pt-4 mt-4 border-t space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium text-muted-foreground">
                      Recently Deleted ({filteredDeletedOfferings.length})
                    </Label>
                    <span className="text-xs text-muted-foreground">
                      Auto-purged after 90 days
                    </span>
                  </div>
                  {filteredDeletedOfferings.map((offering) => {
                    const deletedAt = offering.deletedAt ? new Date(offering.deletedAt) : null;
                    const daysAgo = deletedAt
                      ? Math.max(0, Math.floor((Date.now() - deletedAt.getTime()) / (1000 * 60 * 60 * 24)))
                      : null;
                    return (
                      <Card key={offering.id} className="bg-muted/30">
                        <CardContent className="p-3">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm" data-testid={`text-deleted-offering-name-${offering.id}`}>
                                  {offering.name}
                                </span>
                                {hasMultipleTalent && offering.talentId && (
                                  <Badge variant="outline" className="text-xs">
                                    <User className="w-3 h-3 mr-1" />
                                    {talent.find(e => e.id === offering.talentId)?.name || "Unknown"}
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground mt-1">
                                Deleted {daysAgo !== null ? `${daysAgo}d ago` : "—"}
                                {offering.deletedByEmail ? ` by ${offering.deletedByEmail}` : ""}
                              </div>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => restoreMutation.mutate(offering.id)}
                              disabled={restoreMutation.isPending}
                              data-testid={`button-restore-offering-${offering.id}`}
                            >
                              <RotateCcw className="w-3 h-3 mr-1" />
                              Restore
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </DialogContent>

      {dynamicPricingOfferingId && (
        <DynamicPricingDialog
          open={dynamicPricingDialogOpen}
          onOpenChange={setDynamicPricingDialogOpen}
          eventOfferingId={dynamicPricingOfferingId}
          eventOfferingName={offerings.find(o => o.id === dynamicPricingOfferingId)?.name || "Event Offering"}
          baseRate={offerings.find(o => o.id === dynamicPricingOfferingId)?.rate || "0"}
          regions={regions}
        />
      )}
    </Dialog>
  );
}

interface DynamicPricingDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventOfferingId: string;
  eventOfferingName: string;
  baseRate: string;
  regions: Region[];
}

interface PricingRule {
  id: string;
  clientType: string;
  regionIds: string[];
  rate: string;
  isAvailable: boolean;
  appliesToAllRegions?: boolean;
}

function DynamicPricingDialog({ 
  open, 
  onOpenChange, 
  eventOfferingId, 
  eventOfferingName, 
  baseRate,
  regions 
}: DynamicPricingDialogProps) {
  const { toast } = useToast();
  // Task #107 — workspace-scoped writes must carry the /w/:slug prefix.
  const { buildPath } = useWorkspace();
  const [selectedClientType, setSelectedClientType] = useState<string>("All Client Types");
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);
  const [isAddingRule, setIsAddingRule] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [regionSearch, setRegionSearch] = useState("");
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [appliesToAllRegions, setAppliesToAllRegions] = useState(false);
  const [newRate, setNewRate] = useState(baseRate);
  const [newIsAvailable, setNewIsAvailable] = useState(true);

  const { data: clientTypes = [] } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  const { data: existingPricing = [], isLoading } = useQuery<EventOfferingMarketPricing[]>({
    queryKey: ["/api/event-offerings", eventOfferingId, "market-pricing"],
    enabled: open && !!eventOfferingId,
  });

  const activeClientTypes = clientTypes.filter(ct => ct.isActive);

  // Convert existing pricing to rules format grouped by rate, availability, and clientType
  useEffect(() => {
    if (existingPricing.length > 0) {
      // Group by rate, availability, clientType, AND appliesToAllRegions sentinel
      const ruleMap = new Map<string, PricingRule>();
      existingPricing.forEach(p => {
        const clientType = p.clientType || "All Client Types";
        const isAll = (p as any).appliesToAllRegions === true;
        const key = `${p.rate || baseRate}-${p.isAvailable}-${clientType}-${isAll ? "ALL" : "REG"}`;
        if (ruleMap.has(key)) {
          if (!isAll && p.regionId) ruleMap.get(key)!.regionIds.push(p.regionId);
        } else {
          ruleMap.set(key, {
            id: crypto.randomUUID(),
            clientType: clientType,
            regionIds: isAll ? [] : (p.regionId ? [p.regionId] : []),
            rate: p.rate || baseRate,
            isAvailable: p.isAvailable ?? true,
            appliesToAllRegions: isAll,
          });
        }
      });
      setPricingRules(Array.from(ruleMap.values()));
    }
  }, [existingPricing, baseRate]);

  // Filter rules by selected client type
  const filteredRules = pricingRules.filter(
    r => selectedClientType === "All Client Types" || r.clientType === selectedClientType
  );

  const filteredRegions = regions.filter(r => 
    r.name.toLowerCase().includes(regionSearch.toLowerCase())
  );

  const saveMutation = useMutation({
    mutationFn: async (pricing: Array<{ regionId: string; rate: string | null; isAvailable: boolean; clientType?: string }>) => {
      const response = await apiRequest("POST", buildPath(`/api/event-offerings/${eventOfferingId}/market-pricing/bulk`), { pricing });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/event-offerings", eventOfferingId, "market-pricing"] });
      toast({ title: "Saved", description: "Dynamic pricing rules have been updated." });
      onOpenChange(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to save pricing rules.", variant: "destructive" });
    },
  });

  const handleSave = () => {
    // Convert rules back to either an "all regions" sentinel row or per-region rows
    const pricingToSave: Array<{ regionId?: string | null; appliesToAllRegions?: boolean; rate: string | null; isAvailable: boolean; clientType?: string }> = [];
    pricingRules.forEach(rule => {
      const clientType = rule.clientType === "All Client Types" ? undefined : rule.clientType;
      const rate = rule.rate === baseRate ? null : rule.rate;
      if (rule.appliesToAllRegions) {
        pricingToSave.push({
          appliesToAllRegions: true,
          regionId: null,
          rate,
          isAvailable: rule.isAvailable,
          clientType,
        });
      } else {
        rule.regionIds.forEach(regionId => {
          pricingToSave.push({
            regionId,
            rate,
            isAvailable: rule.isAvailable,
            clientType,
          });
        });
      }
    });
    saveMutation.mutate(pricingToSave as any);
  };

  const handleAddRule = () => {
    if (!appliesToAllRegions && selectedRegions.length === 0) {
      toast({ title: "Select Regions", description: "Please select at least one region for this rule, or use \"All Regions (auto)\".", variant: "destructive" });
      return;
    }
    const newRule: PricingRule = {
      id: crypto.randomUUID(),
      clientType: selectedClientType,
      regionIds: appliesToAllRegions ? [] : selectedRegions,
      rate: newRate,
      isAvailable: newIsAvailable,
      appliesToAllRegions,
    };
    setPricingRules(prev => [...prev, newRule]);
    resetForm();
  };

  const handleUpdateRule = () => {
    if (!editingRuleId) return;
    setPricingRules(prev => prev.map(r => 
      r.id === editingRuleId
        ? {
            ...r,
            regionIds: appliesToAllRegions ? [] : selectedRegions,
            rate: newRate,
            isAvailable: newIsAvailable,
            clientType: selectedClientType,
            appliesToAllRegions,
          }
        : r
    ));
    resetForm();
  };

  const handleDeleteRule = (ruleId: string) => {
    setPricingRules(prev => prev.filter(r => r.id !== ruleId));
  };

  const handleEditRule = (rule: PricingRule) => {
    setEditingRuleId(rule.id);
    setSelectedRegions(rule.regionIds);
    setAppliesToAllRegions(rule.appliesToAllRegions === true);
    setNewRate(rule.rate);
    setNewIsAvailable(rule.isAvailable);
    setSelectedClientType(rule.clientType); // Restore the client type when editing
    setIsAddingRule(true);
  };

  const resetForm = () => {
    setIsAddingRule(false);
    setEditingRuleId(null);
    setSelectedRegions([]);
    setAppliesToAllRegions(false);
    setNewRate(baseRate);
    setNewIsAvailable(true);
    setRegionSearch("");
    setSelectedClientType("All Client Types"); // Reset to default
  };

  const toggleRegion = (regionId: string) => {
    setSelectedRegions(prev => 
      prev.includes(regionId) ? prev.filter(id => id !== regionId) : [...prev, regionId]
    );
  };

  const selectAllRegions = () => {
    setSelectedRegions(regions.map(r => r.id));
  };

  const clearAllRegions = () => {
    setSelectedRegions([]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Dynamic Pricing
          </DialogTitle>
          <DialogDescription className="space-y-2">
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="secondary" className="text-sm font-medium" data-testid="badge-event-offering-name">
                {eventOfferingName}
              </Badge>
              <span className="text-muted-foreground">•</span>
              <span>Base rate: ${parseFloat(baseRate).toFixed(2)}</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Configure custom pricing rules for different client types and regions.
            </p>
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="text-center py-4 text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-4">
            {/* Client Type Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Client Type</Label>
              <Select value={selectedClientType} onValueChange={setSelectedClientType}>
                <SelectTrigger data-testid="select-client-type-pricing">
                  <SelectValue placeholder="Select client type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All Client Types">All Client Types</SelectItem>
                  {activeClientTypes.map(ct => (
                    <SelectItem key={ct.id} value={ct.name}>{ct.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Add Rule Button */}
            {!isAddingRule && (
              <Button 
                variant="outline" 
                onClick={() => setIsAddingRule(true)}
                className="w-full"
                data-testid="button-add-pricing-rule"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Dynamic Pricing Rule
              </Button>
            )}

            {/* Add/Edit Rule Form */}
            {isAddingRule && (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">
                      {editingRuleId ? "Edit Pricing Rule" : "New Pricing Rule"}
                    </Label>
                    <Badge variant="secondary">{selectedClientType}</Badge>
                  </div>

                  {/* Region Selection */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm">Select Regions</Label>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={selectAllRegions}
                          disabled={appliesToAllRegions}
                          data-testid="button-select-all-regions"
                        >
                          Select All
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={clearAllRegions}
                          disabled={appliesToAllRegions}
                          data-testid="button-clear-regions"
                        >
                          Clear
                        </Button>
                      </div>
                    </div>

                    {/* All Regions (auto-include new) sentinel toggle */}
                    <div
                      className={`flex items-start gap-2 p-2 rounded-md border ${appliesToAllRegions ? "border-primary bg-primary/5" : "border-border"} cursor-pointer`}
                      onClick={() => setAppliesToAllRegions(prev => !prev)}
                      data-testid="toggle-all-regions-auto"
                    >
                      <Checkbox
                        checked={appliesToAllRegions}
                        onCheckedChange={(v) => setAppliesToAllRegions(v === true)}
                        data-testid="checkbox-all-regions-auto"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">All Regions (auto-include new)</div>
                        <div className="text-xs text-muted-foreground">
                          Applies to every region — including any region added later.
                        </div>
                      </div>
                    </div>

                    <Input
                      placeholder="Search regions..."
                      value={regionSearch}
                      onChange={(e) => setRegionSearch(e.target.value)}
                      className="h-8"
                      disabled={appliesToAllRegions}
                      data-testid="input-region-search-pricing"
                    />
                    <div className={`max-h-40 overflow-y-auto border rounded-md p-2 space-y-1 ${appliesToAllRegions ? "opacity-50 pointer-events-none" : ""}`}>
                      {filteredRegions.length === 0 ? (
                        <div className="text-sm text-muted-foreground text-center py-2">No regions found</div>
                      ) : (
                        filteredRegions.map(region => (
                          <div 
                            key={region.id}
                            className="flex items-center space-x-2 p-1.5 rounded hover:bg-muted cursor-pointer"
                            onClick={() => !appliesToAllRegions && toggleRegion(region.id)}
                          >
                            <Checkbox 
                              checked={appliesToAllRegions || selectedRegions.includes(region.id)}
                              onCheckedChange={() => toggleRegion(region.id)}
                              disabled={appliesToAllRegions}
                              data-testid={`checkbox-region-${region.id}`}
                            />
                            <span className="text-sm flex-1">{region.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                    {appliesToAllRegions ? (
                      <div className="text-xs text-muted-foreground" data-testid="text-region-count">
                        All regions (auto-includes new ones)
                      </div>
                    ) : selectedRegions.length > 0 ? (
                      <div className="text-xs text-muted-foreground" data-testid="text-region-count">
                        {selectedRegions.length} region{selectedRegions.length > 1 ? "s" : ""} selected
                      </div>
                    ) : null}
                  </div>

                  {/* Rate Input */}
                  <div className="space-y-2">
                    <Label className="text-sm">Rate</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-sm">$</span>
                      <Input
                        type="number"
                        min={0}
                        step="0.01"
                        value={newRate}
                        onChange={(e) => setNewRate(e.target.value)}
                        className="w-32"
                        data-testid="input-new-rate"
                      />
                    </div>
                  </div>

                  {/* Availability Toggle */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id="new-available"
                      checked={newIsAvailable}
                      onCheckedChange={setNewIsAvailable}
                      data-testid="switch-new-available"
                    />
                    <Label htmlFor="new-available" className="text-sm">
                      {newIsAvailable ? "Available in these regions" : "Not available in these regions"}
                    </Label>
                  </div>

                  {/* Form Actions */}
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={resetForm} data-testid="button-cancel-rule">
                      Cancel
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={editingRuleId ? handleUpdateRule : handleAddRule}
                      disabled={!appliesToAllRegions && selectedRegions.length === 0}
                      data-testid="button-save-rule"
                    >
                      {editingRuleId ? "Update Rule" : "Add Rule"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Existing Rules List */}
            {filteredRules.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-muted-foreground">
                  Pricing Rules ({filteredRules.length})
                </Label>
                {filteredRules.map(rule => (
                  <Card key={rule.id}>
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant={rule.isAvailable ? "default" : "secondary"}>
                              {rule.isAvailable ? `$${parseFloat(rule.rate).toFixed(2)}` : "Not Available"}
                            </Badge>
                            <Badge variant="outline" className="text-xs">
                              {rule.clientType}
                            </Badge>
                          </div>
                          <div className="mt-2 flex items-center gap-1 flex-wrap">
                            <MapPin className="w-3 h-3 text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground">
                              {rule.appliesToAllRegions
                                ? "All Regions (auto-includes new)"
                                : rule.regionIds.length === regions.length
                                  ? "All Regions"
                                  : rule.regionIds.slice(0, 3).map(id => regions.find(r => r.id === id)?.name).filter(Boolean).join(", ")}
                              {!rule.appliesToAllRegions && rule.regionIds.length > 3 && ` +${rule.regionIds.length - 3} more`}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleEditRule(rule)}
                            data-testid={`button-edit-rule-${rule.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => handleDeleteRule(rule.id)}
                            data-testid={`button-delete-rule-${rule.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-muted-foreground" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {filteredRules.length === 0 && !isAddingRule && (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No pricing rules yet. Click "Add Dynamic Pricing Rule" to create one.
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-cancel-pricing">
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saveMutation.isPending}
            data-testid="button-save-pricing"
          >
            {saveMutation.isPending ? "Saving..." : "Save Pricing"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
